// Small formatting/validation helpers for the Audits confirm form.
import { formatNumbersWithComma } from '../../../util/formatUtils';

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

/** Drop a leading URL scheme so a `https://`-prefixed field doesn't double up on paste. */
export const stripScheme = (value = '') => String(value).replace(SCHEME_RE, '');

/** Keep digits only. */
export const digitsOnly = (value = '') => String(value).replace(/\D/g, '');

/** Digits + thousands separators, live-friendly (e.g. "48000" → "48,000"). */
export const formatSpend = (value = '') => formatNumbersWithComma(digitsOnly(value));

/** Digits only — the raw form the backend expects ("numbers with no symbols"). */
export const stripCommas = (value = '') => digitsOnly(value);

/** Abbreviate a number for the outcome chips: 400000 → "400k", 1500000 → "1.5M", 480 → "480". */
export const abbreviateOutcome = (value = '') => {
  const digits = digitsOnly(value);
  if (!digits) return '';
  const n = Number(digits);
  if (n >= 1000000) {
    const m = n / 1000000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (n >= 1000) {
    const k = n / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return String(n);
};

// Adherence-score bands (0–100), per Figma ranges 878-24638:
//   poor <45 · fair 45–65 · good 65–85 · excellent ≥85.
export const SCORE_BAND_LABELS = {
  poor: 'Poor',
  fair: 'Fair',
  good: 'Good',
  excellent: 'Excellent',
};

/** Map an adherence score (0–100) to its band key. */
export const scoreBand = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'poor';
  if (n < 45) return 'poor';
  if (n < 65) return 'fair';
  if (n < 85) return 'good';
  return 'excellent';
};

/** Bare host from a URL-ish string (scheme optional, `www.` and path stripped). */
export const domainFromUrl = (value = '') => {
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  const withScheme = SCHEME_RE.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withScheme).hostname.replace(/^www\./, '');
  } catch {
    const host = trimmed.replace(SCHEME_RE, '').split('/')[0];
    return host.replace(/^www\./, '');
  }
};

/** Google favicon URL for a domain (sz=128), or '' when no domain. Mirrors CI / BrandSafety. */
export const faviconUrl = (domain = '') =>
  domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : '';

/**
 * Resolve an audit card's logo the way Competitive Intel does (`mapProducts`):
 * BE logo first (`card.image` ⇐ `productLogos`), else the Google favicon derived
 * from a domain (`card.domain` ⇐ `productDomains`, or the subject URL's host),
 * else `null` (caller shows an initials fallback).
 */
export const resolveAuditLogo = (card = {}) =>
  card.image || faviconUrl(card.domain || domainFromUrl(card.subjectUrl)) || null;

// Host substring → audit platform value (matches PLATFORM_OPTIONS values, 'x' for Twitter/X).
const PLATFORM_BY_HOST = [
  [/instagram\.com/i, 'instagram'],
  [/tiktok\.com/i, 'tiktok'],
  [/(youtube\.com|youtu\.be)/i, 'youtube'],
  [/(twitter\.com|x\.com)/i, 'x'],
  [/reddit\.com/i, 'reddit'],
  [/(facebook\.com|fb\.com)/i, 'facebook'],
];

/**
 * Best-effort parse of a creator profile URL into an audit `{ handle, platform }`
 * so the influencer form can prefill from a pasted link. `platform` maps to the
 * PLATFORM_OPTIONS values ('x' for Twitter/X); both are '' when undeterminable.
 */
export const parseInfluencerUrl = (value = '') => {
  const trimmed = String(value).trim();
  if (!trimmed) return { handle: '', platform: '' };
  const withScheme = SCHEME_RE.test(trimmed) ? trimmed : `https://${trimmed}`;
  let host = '';
  let path = '';
  try {
    const url = new URL(withScheme);
    host = url.hostname.replace(/^www\./, '');
    path = url.pathname;
  } catch {
    const rest = trimmed.replace(SCHEME_RE, '');
    host = rest.split('/')[0].replace(/^www\./, '');
    path = rest.slice(host.length);
  }
  const platform = (PLATFORM_BY_HOST.find(([re]) => re.test(host)) || [])[1] || '';
  const segments = path
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  let handle = '';
  if (segments.length) {
    // YouTube uses /@handle, /c/name, /channel/id, /user/name; others use /@handle or /handle.
    const [first, second] = segments;
    handle = ['c', 'channel', 'user'].includes(first.toLowerCase()) && second ? second : first;
    handle = handle.replace(/^@/, '');
  }
  return { handle, platform };
};

// Audit platform value → schemeless profile URL prefix (inverse of PLATFORM_BY_HOST).
const PROFILE_URL_PREFIX = {
  youtube: 'www.youtube.com/@',
  instagram: 'www.instagram.com/',
  tiktok: 'www.tiktok.com/@',
  x: 'x.com/',
  twitter: 'x.com/',
  reddit: 'www.reddit.com/user/',
  facebook: 'www.facebook.com/',
};

/**
 * Build a schemeless creator profile URL from an audit `platform` + `handle` (the
 * form shows its own https:// prefix). Round-trips through `parseInfluencerUrl`;
 * '' when either is empty or the platform has no known profile URL (e.g. 'website').
 */
export const buildInfluencerProfileUrl = (platform = '', handle = '') => {
  const key = String(platform || '')
    .trim()
    .toLowerCase();
  const prefix = PROFILE_URL_PREFIX[key];
  const name = String(handle || '')
    .trim()
    .replace(/^@/, '');
  if (!prefix || !name) return '';
  return `${prefix}${encodeURIComponent(name)}`;
};

/** True when `value` (scheme optional) parses as a well-formed URL with a dotted host. */
export const isValidUrl = (value = '') => {
  const trimmed = String(value).trim();
  if (!trimmed) return false;
  const withScheme = SCHEME_RE.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    return Boolean(url.hostname) && url.hostname.includes('.');
  } catch {
    return false;
  }
};
